import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { scrollToSection } from "../utils/scroll";

const LINKS = [
  { label: "Technik", target: "#technik" },
  { label: "REVIVO", target: "#revivo" },
  { label: "Über uns", target: "#story" },
  { label: "Kontakt", target: "#kontakt" },
];

export default function NavigationHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (target) => {
    setMobileOpen(false);
    scrollToSection(target);
  };

  const openConfigurator = () => {
    setMobileOpen(false);
    window.open("https://konfigurator.cool-pool-company.de/", "_blank", "noopener");
  };

  return (
    <motion.header
      data-testid="main-navigation-bar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-[#050B14]/80 backdrop-blur-xl border-b border-white/10 py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between">
        <button
          data-testid="nav-logo-button"
          onClick={() => window.__lenis?.scrollTo(0)}
          className="flex items-center gap-2.5 group"
        >
          <img
            src="/images/logo.png"
            alt="the coolpool company GmbH"
            className="h-9 sm:h-10 w-auto notranslate group-hover:drop-shadow-[0_0_14px_rgba(0,229,255,0.35)] transition-all duration-300"
            translate="no"
          />
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => (
            <button
              key={l.target}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              onClick={() => go(l.target)}
              className="text-sm text-slate-300 hover:text-cyan-300 transition-colors duration-300 tracking-wide"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-configurator-cta-button"
            onClick={openConfigurator}
            className="hidden sm:inline-flex items-center gap-2 cyan-glow-btn text-[#050B14] font-semibold text-sm px-5 py-2.5 rounded-full"
          >
            Pool konfigurieren <ArrowUpRight className="w-4 h-4" />
          </button>
          <button
            data-testid="nav-mobile-menu-button"
            onClick={() => setMobileOpen((v) => !v)}
            className="lg:hidden w-10 h-10 rounded-full border border-white/15 flex items-center justify-center"
            aria-label="Menü"
          >
            {mobileOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-[#050B14]/95 backdrop-blur-xl border-t border-white/10 mt-3">
          <div className="px-6 py-4 flex flex-col gap-1">
            {LINKS.map((l) => (
              <button
                key={l.target}
                data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                onClick={() => go(l.target)}
                className="text-left py-3 text-slate-200 border-b border-white/5 last:border-0"
              >
                {l.label}
              </button>
            ))}
            <button
              data-testid="nav-mobile-configurator-cta-button"
              onClick={openConfigurator}
              className="cyan-glow-btn text-[#050B14] font-semibold text-sm px-5 py-3 rounded-full mt-3 inline-flex items-center justify-center gap-2"
            >
              Pool konfigurieren <ArrowUpRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </motion.header>
  );
}
